import BaseAPI from './base.api';
import { type AxiosError } from 'axios';
import {
  type MakeModelDetails,
  type MakeModelUpdatePayload,
} from 'types/makeModel.types';

/**
 * Raw API response interface (what the API actually returns)
 * Note: current_capacity comes as string from API but needs conversion to number
 */
interface MakeModelDetailsRaw {
  customerId: string;
  id: string;
  make: string;
  model: string;
  idle_rpm: number | null;
  idle_threshold: number;
  excess_idle_threshold: number;
  fuel_source: string | null;
  max_payload_capacity: number | null;
  current_capacity: string | null; // API returns this as string
  patternName: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Custom error class for Make Model API operations
 */
export class MakeModelAPIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public originalError?: AxiosError
  ) {
    super(message);
    this.name = 'MakeModelAPIError';
  }
}

/**
 * API class for managing Make Model operations
 * Handles communication with the backend API for vehicle make/model configurations
 */
export default class MakeModelAPI extends BaseAPI {
  /**
   * Get make model details for a specific customer
   * @param customerId - The customer ID
   * @returns Promise<MakeModelDetailsRaw[]> - Raw API response data
   * @throws {MakeModelAPIError} When the request fails
   */
  async getMakeModelDetails(
    customerId: string
  ): Promise<MakeModelDetailsRaw[]> {
    if (!customerId) {
      throw new MakeModelAPIError('Customer ID is required');
    }

    try {
      const response = await this.API.get(
        `/make-model-details/customer/${customerId}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof MakeModelAPIError) {
        throw error;
      }
      throw new MakeModelAPIError(
        'Failed to fetch make model details',
        undefined,
        error as AxiosError
      );
    }
  }

  /**
   * Update make model details
   * @param id - The make model ID
   * @param payload - The update payload
   * @returns Promise<MakeModelDetails> - Updated make model details
   * @throws {MakeModelAPIError} When the request fails
   */
  async updateMakeModelDetails(
    id: string,
    payload: MakeModelUpdatePayload
  ): Promise<MakeModelDetails> {
    if (!id) {
      throw new MakeModelAPIError('Make model ID is required');
    }

    if (!payload || Object.keys(payload).length === 0) {
      throw new MakeModelAPIError('Update payload cannot be empty');
    }

    try {
      const response = await this.API.put(`/make-model-details/${id}`, payload);
      return response.data;
    } catch (error) {
      if (error instanceof MakeModelAPIError) {
        throw error;
      }
      throw new MakeModelAPIError(
        'Failed to update make model details',
        undefined,
        error as AxiosError
      );
    }
  }
}
